import { useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { useParams, useNavigate } from "react-router";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers";
import { formatISO, parseISO } from "date-fns";
import {
  useGetMediaTitlesQuery,
  useGetBenchmarkReportsQuery,
  useUpdateBenchmarkReportMutation,
  useCreateBenchmarkTableMutation,
  useGetBenchmarkTablesQuery,
  useDeleteBenchmarkTableMutation,
} from "../services/api";

const formatRange = (range, options = {}) => {
  if (!range || range.count === 0) return "—";
  const fmt = (n) => n.toLocaleString(undefined, options);
  if (range.min === range.max) return fmt(range.min);
  return `${fmt(range.min)} – ${fmt(range.max)}`;
};

const formatDate = (d) =>
  new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const TableRows = ({ metrics }) => {
  if (!metrics || metrics.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ mt: 1 }}>
        No data.
      </Typography>
    );
  }

  const packageTypes = [
    ...new Set(metrics.flatMap(({ values }) => Object.keys(values))),
  ];

  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Package Type</strong>
            </TableCell>
            {metrics.map(({ metric }) => (
              <TableCell key={metric} align="right" sx={{ textTransform: "capitalize" }}>
                <strong>{metric}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {packageTypes.map((packageType) => (
            <TableRow key={packageType}>
              <TableCell sx={{ textTransform: "capitalize" }}>
                {packageType}
              </TableCell>
              {metrics.map(({ metric, values }) => {
                const [range] = values[packageType] ?? [null];
                return (
                  <Tooltip key={metric} title={range ? `${range.count} project(s)` : ""}>
                    <TableCell align="right">{formatRange(range)}</TableCell>
                  </Tooltip>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BenchmarkTableCard = ({ table, reportId, allMediaTitles }) => {
  const [deleteTable, { isLoading: deleting }] = useDeleteBenchmarkTableMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
          {table.mediaTitles.map((domain) => {
            const found = allMediaTitles?.find((mt) => mt.domain === domain);
            return <Chip key={domain} label={found?.label ?? domain} size="small" />;
          })}
        </Box>
        <IconButton
          size="small"
          onClick={() => setConfirmOpen(true)}
          disabled={deleting}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="caption" color="textSecondary">
        Last refreshed: {table.refreshedAt ? formatDate(table.refreshedAt) : "—"}
      </Typography>
      <TableRows metrics={table.metric} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Table"
        message="Are you sure you want to delete this table?"
        onConfirm={() => { deleteTable({ reportId, tableId: table._id }); setConfirmOpen(false); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Paper>
  );
};

const TablesSection = ({ reportId, allMediaTitles }) => {
  const { data: tables, isLoading } = useGetBenchmarkTablesQuery(reportId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!tables || tables.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ mt: 1 }}>
        No tables yet. Add one below.
      </Typography>
    );
  }

  return (
    <Box>
      {tables.map((table) => (
        <BenchmarkTableCard key={table._id} table={table} reportId={reportId} allMediaTitles={allMediaTitles} />
      ))}
    </Box>
  );
};

const PRODUCT_TYPE_OPTIONS = [
  { value: "social-product", label: "Social Product" },
  { value: "social-link", label: "Social Links" },
];

const METRIC_OPTIONS = [
  { value: "impressions", label: "Impressions" },
  { value: "clicks", label: "Clicks" },
  { value: "linkClicks", label: "Link Clicks" },
  { value: "views", label: "Views" },
];

const AddTableForm = ({ reportId, mediaTitles }) => {
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [productType, setProductType] = useState("social-product");
  const [selectedMetrics, setSelectedMetrics] = useState(["impressions"]);
  const [createTable, { isLoading }] = useCreateBenchmarkTableMutation();

  const handleAdd = async () => {
    if (selectedDomains.length === 0 || selectedMetrics.length === 0) return;
    await createTable({ reportId, mediaTitles: selectedDomains, metrics: selectedMetrics, type: productType });
    setSelectedDomains([]);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Add Table
      </Typography>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Media Titles</InputLabel>
          <Select
            multiple
            value={selectedDomains}
            onChange={(e) => setSelectedDomains(e.target.value)}
            input={<OutlinedInput label="Media Titles" />}
            renderValue={(selected) =>
              selected
                .map(
                  (domain) =>
                    mediaTitles.find((mt) => mt.domain === domain)?.label ?? domain,
                )
                .join(", ")
            }
          >
            {(mediaTitles ?? []).map((mt) => (
              <MenuItem key={mt.domain} value={mt.domain}>
                {mt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Product Type</InputLabel>
          <Select
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            input={<OutlinedInput label="Product Type" />}
          >
            {PRODUCT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Metrics</InputLabel>
          <Select
            multiple
            value={selectedMetrics}
            onChange={(e) => setSelectedMetrics(e.target.value)}
            input={<OutlinedInput label="Metrics" />}
            renderValue={(selected) =>
              selected
                .map((v) => METRIC_OPTIONS.find((o) => o.value === v)?.label ?? v)
                .join(", ")
            }
          >
            {METRIC_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={isLoading || selectedDomains.length === 0 || selectedMetrics.length === 0}
        >
          {isLoading ? <CircularProgress size={20} /> : "Add Table"}
        </Button>
      </Box>
    </Box>
  );
};

const BenchmarkReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const { data: mediaTitles, isLoading: mediaTitlesLoading } = useGetMediaTitlesQuery();
  const { data: reports } = useGetBenchmarkReportsQuery();
  const [updateReport, { isLoading: updating }] = useUpdateBenchmarkReportMutation();

  const report = reports?.find((r) => r._id === reportId);

  const handleEditOpen = () => {
    setName(report?.name ?? "");
    setDateFrom(report ? parseISO(report.daterange.from) : null);
    setDateTo(report ? parseISO(report.daterange.to) : null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!name || !dateFrom || !dateTo) return;
    await updateReport({
      reportId,
      name,
      dateFrom: formatISO(dateFrom, { representation: "date" }),
      dateTo: formatISO(dateTo, { representation: "date" }),
    });
    setEditing(false);
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/benchmark")}
          size="small"
        >
          All Reports
        </Button>
      </Box>

      {editing ? (
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          />
          <DatePicker
            label="Date From"
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="Date To"
            value={dateTo}
            onChange={setDateTo}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updating || !name || !dateFrom || !dateTo}
          >
            {updating ? <CircularProgress size={20} /> : "Save"}
          </Button>
          <Button onClick={() => setEditing(false)} disabled={updating}>
            Cancel
          </Button>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h5">
            {report?.name ?? "Report"}
          </Typography>
          {report && (
            <IconButton size="small" onClick={handleEditOpen}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {report && !editing && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {formatDate(report.daterange.from)} – {formatDate(report.daterange.to)}
          {" · "}Created by {report.createdBy.name} on {formatDate(report.createdAt)}
        </Typography>
      )}

      <TablesSection reportId={reportId} allMediaTitles={mediaTitles} />

      {!mediaTitlesLoading && (
        <AddTableForm reportId={reportId} mediaTitles={mediaTitles ?? []} />
      )}
    </Box>
  );
};

export default BenchmarkReportPage;